import { useState, useEffect } from "react";
import { 
  Share2, Copy, Check, Globe, Lock, Loader2, Download, 
  Mail, FileDown, UserPlus, Link2, X, Lightbulb, Palette, 
  Quote, FileText, Pencil, Sparkles, StickyNote, AlertTriangle,
  Highlighter, BookOpen, Users, Wrench, RefreshCw
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
// jsPDF is dynamically imported when needed to reduce initial bundle size

interface ShareDialogProps {
  analysisId: string;
  isShared: boolean;
  shareToken: string | null;
  onShareChange: (isShared: boolean, shareToken: string | null) => void;
  analysisData?: {
    title: string;
    keyIdeas?: string[];
    themes?: Array<{ name: string; explanation: string }>;
    importantQuotes?: Array<{ quote: string; context: string }>;
    summary?: string[];
    authorIntent?: string;
    writingTechniques?: string[];
    characters?: Array<{ name: string; role?: string; description?: string; traits?: string[] }>;
    literaryTechniques?: Array<{ name: string; example?: string; explanation?: string; purpose?: string }>;
  };
  notes?: string;
  extractedText?: string;
  // External control props for opening dialog programmatically (e.g., from sidebar)
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}

type ExportSection = "annotate" | "keyIdeas" | "themes" | "quotes" | "characters" | "literaryTechniques" | "summary" | "intent" | "techniques" | "notes";

const exportSections: { id: ExportSection; label: string; icon: typeof Lightbulb }[] = [
  { id: "annotate", label: "Annotate (Highlighted Text)", icon: Highlighter },
  { id: "keyIdeas", label: "Key Ideas", icon: Lightbulb },
  { id: "themes", label: "Themes", icon: Palette },
  { id: "quotes", label: "Quotes", icon: Quote },
  { id: "characters", label: "Characters", icon: Users },
  { id: "literaryTechniques", label: "Literary Techniques", icon: Wrench },
  { id: "summary", label: "Summary", icon: FileText },
  { id: "intent", label: "Author Intent", icon: Pencil },
  { id: "techniques", label: "Writing Techniques", icon: Sparkles },
  { id: "notes", label: "Personal Notes", icon: StickyNote },
];

interface InvitedUser {
  id: string;
  email: string;
  accessLevel: "view" | "edit";
  createdAt: string;
  acceptedAt: string | null;
}

export function ShareDialog({ 
  analysisId, 
  isShared, 
  shareToken, 
  onShareChange, 
  analysisData,
  notes,
  extractedText,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  hideTrigger = false
}: ShareDialogProps) {
  const { tier, isAdmin } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState("invite");
  const [selectedSections, setSelectedSections] = useState<Set<ExportSection>>(
    new Set(["keyIdeas", "themes", "quotes", "summary", "intent"])
  );
  const [exporting, setExporting] = useState(false);
  const [exportingDocx, setExportingDocx] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitedUsers, setInvitedUsers] = useState<InvitedUser[]>([]);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [internalDialogOpen, setInternalDialogOpen] = useState(false);
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null);
  
  // Use external control if provided, otherwise use internal state
  const dialogOpen = externalOpen !== undefined ? externalOpen : internalDialogOpen;
  const setDialogOpen = externalOnOpenChange || setInternalDialogOpen;

  // Fetch existing invites when dialog opens
  useEffect(() => {
    if (dialogOpen && analysisId) {
      fetchInvites();
    }
  }, [dialogOpen, analysisId]);

  const fetchInvites = async () => {
    setLoadingInvites(true);
    try {
      const { data, error } = await supabase
        .from("analysis_invites")
        .select("id, email, access_level, created_at, accepted_at")
        .eq("analysis_id", analysisId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setInvitedUsers(
        (data || []).map((invite) => ({
          id: invite.id,
          email: invite.email,
          accessLevel: invite.access_level as "view" | "edit",
          createdAt: invite.created_at,
          acceptedAt: invite.accepted_at,
        }))
      );
    } catch (error) {
      console.error("Error fetching invites:", error);
    } finally {
      setLoadingInvites(false);
    }
  };

  const generateToken = () => {
    const array = new Uint8Array(24);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };

  const getShareUrl = (token: string) => {
    return `${window.location.origin}/shared/${token}`;
  };

  const handleToggleShare = async (enabled: boolean) => {
    setLoading(true);
    try {
      const newToken = enabled ? (shareToken || generateToken()) : shareToken;
      
      const { error } = await supabase
        .from("analyses")
        .update({
          is_shared: enabled,
          share_token: newToken,
        })
        .eq("id", analysisId);

      if (error) throw error;
      
      onShareChange(enabled, newToken);
      toast.success(enabled ? "Link sharing enabled" : "Link sharing disabled");
    } catch (error) {
      console.error("Error updating share settings:", error);
      toast.error("Failed to update sharing settings");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareToken) return;
    
    try {
      await navigator.clipboard.writeText(getShareUrl(shareToken));
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) return;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emails = inviteEmail.split(",").map(e => e.trim().toLowerCase()).filter(e => e);
    
    const invalidEmails = emails.filter(e => !emailRegex.test(e) || e.length > 255);
    if (invalidEmails.length > 0) {
      toast.error(`Invalid email: ${invalidEmails[0]}`);
      return;
    }

    if (emails.length > 10) {
      toast.error("Maximum 10 invites at once");
      return;
    }

    setSendingInvite(true);
    
    try {
      const { data, error } = await supabase.functions.invoke("send-invite", {
        body: {
          analysisId,
          analysisTitle: analysisData?.title || "Untitled",
          emails,
          accessLevel: "view",
        },
      });

      if (error) throw error;

      if (data.error) {
        toast.error(data.error);
      } else {
        const successCount = data.successCount || 0;
        if (successCount > 0) {
          toast.success(`${successCount} invite${successCount > 1 ? "s" : ""} sent!`);
          await fetchInvites();
        }
        
        // Show any individual errors
        const failures = data.results?.filter((r: { success: boolean }) => !r.success) || [];
        failures.forEach((f: { email: string; error?: string }) => {
          toast.error(`${f.email}: ${f.error || "Failed"}`);
        });
      }
      
      setInviteEmail("");
    } catch (error) {
      console.error("Error sending invites:", error);
      toast.error("Failed to send invites");
    } finally {
      setSendingInvite(false);
    }
  };

  const handleRemoveInvite = async (inviteId: string, email: string) => {
    try {
      const { error } = await supabase
        .from("analysis_invites")
        .delete()
        .eq("id", inviteId);

      if (error) throw error;

      setInvitedUsers(prev => prev.filter(u => u.id !== inviteId));
      toast.success(`Removed access for ${email}`);
    } catch (error) {
      console.error("Error removing invite:", error);
      toast.error("Failed to remove access");
    }
  };

  const handleResendInvite = async (inviteId: string, email: string) => {
    setResendingInviteId(inviteId);
    try {
      const { data, error } = await supabase.functions.invoke("send-invite", {
        body: {
          analysisId,
          analysisTitle: analysisData?.title || "Untitled",
          emails: [email],
          accessLevel: "view",
          resendInviteId: inviteId,
        },
      });

      if (error) throw error;

      if (data.error) {
        toast.error(data.error);
      } else if (data.successCount > 0) {
        toast.success(`Invite resent to ${email}!`);
      } else {
        const failure = data.results?.find((r: { email: string }) => r.email === email);
        toast.error(failure?.error || "Failed to resend invite");
      }
    } catch (error) {
      console.error("Error resending invite:", error);
      toast.error("Failed to resend invite");
    } finally {
      setResendingInviteId(null);
    }
  };

  const toggleSection = (section: ExportSection) => {
    const newSet = new Set(selectedSections);
    if (newSet.has(section)) {
      newSet.delete(section);
    } else {
      newSet.add(section);
    }
    setSelectedSections(newSet);
  };

  const selectAll = () => {
    setSelectedSections(new Set(exportSections.map(s => s.id)));
  };

  const deselectAll = () => {
    setSelectedSections(new Set());
  };

  const generatePDF = async () => {
    if (!analysisData) {
      toast.error("No analysis data to export");
      return;
    }

    setExporting(true);
    try {
      // Dynamically import jsPDF only when needed
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - margin * 2;
      let y = margin;
      let isFirstPage = true;

      // Helper: Safely convert any value to a non-empty string for jsPDF
      const safeText = (text: unknown): string => {
        if (text === null || text === undefined) return "";
        return String(text).trim();
      };

      // Helper: Draw branded header on first page only
      const addBrandedHeader = () => {
        if (!isFirstPage) return;
        
        // Draw book icon (simple representation)
        doc.setDrawColor(20, 184, 166); // Teal color
        doc.setFillColor(20, 184, 166);
        doc.rect(margin, y - 3, 3, 10, 'F');
        doc.rect(margin + 4, y - 3, 3, 10, 'F');
        doc.setDrawColor(0);
        
        // AnnotateAI text
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(20, 184, 166);
        doc.text("AnnotateAI", margin + 12, y + 4);
        
        // Document title on right
        const title = analysisData.title || "Analysis Export";
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100);
        const titleWidth = doc.getTextWidth(title);
        const maxTitleWidth = contentWidth / 2;
        const displayTitle = titleWidth > maxTitleWidth 
          ? title.substring(0, 40) + "..." 
          : title;
        doc.text(displayTitle, pageWidth - margin - doc.getTextWidth(displayTitle), y + 4);
        
        y += 12;
        
        // Date
        doc.setFontSize(10);
        doc.setTextColor(128);
        doc.text(`Generated on ${new Date().toLocaleDateString()}`, margin, y);
        y += 8;
        
        // Separator line
        doc.setDrawColor(200);
        doc.setLineWidth(0.5);
        doc.line(margin, y, pageWidth - margin, y);
        y += 10;
        
        doc.setTextColor(0);
        isFirstPage = false;
      };

      // Add branded header
      addBrandedHeader();

      // Helper: Check page and add new page if needed
      const checkPage = () => {
        if (y > 270) {
          doc.addPage();
          y = margin;
        }
      };

      // Helper functions
      const addSection = (sectionTitle: string, content: string[]) => {
        checkPage();

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(sectionTitle, margin, y);
        y += 8;

        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");

        content.forEach((item, index) => {
          const itemText = safeText(item);
          if (!itemText) return; // Skip empty items
          
          const lines = doc.splitTextToSize(`${index + 1}. ${itemText}`, contentWidth);
          
          if (y + lines.length * 6 > 280) {
            doc.addPage();
            y = margin;
          }
          
          doc.text(lines, margin, y);
          y += lines.length * 6 + 4;
        });

        y += 8;
      };

      const addQuoteSection = (sectionTitle: string, quotes: Array<{ quote: string; context: string }>) => {
        checkPage();

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(sectionTitle, margin, y);
        y += 8;

        doc.setFontSize(11);

        quotes.forEach((item) => {
          const quoteText = safeText(item.quote);
          const contextText = safeText(item.context);
          if (!quoteText) return; // Skip empty quotes
          
          const quoteLines = doc.splitTextToSize(`"${quoteText}"`, contentWidth - 10);
          
          if (y + quoteLines.length * 6 + 12 > 280) {
            doc.addPage();
            y = margin;
          }
          
          doc.setFont("helvetica", "bolditalic");
          doc.text(quoteLines, margin + 5, y);
          y += quoteLines.length * 6 + 2;

          if (contextText) {
            doc.setFont("helvetica", "normal");
            doc.setTextColor(100);
            const contextLines = doc.splitTextToSize(`— ${contextText}`, contentWidth - 10);
            doc.text(contextLines, margin + 5, y);
            doc.setTextColor(0);
            y += contextLines.length * 6 + 8;
          } else {
            y += 8;
          }
        });

        y += 8;
      };

      const addThemeSection = (sectionTitle: string, themes: Array<{ name: string; explanation: string }>) => {
        checkPage();

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(sectionTitle, margin, y);
        y += 8;

        doc.setFontSize(11);

        themes.forEach((theme) => {
          const themeName = safeText(theme.name);
          const themeExplanation = safeText(theme.explanation);
          if (!themeName) return; // Skip themes without names
          
          if (y + 20 > 280) {
            doc.addPage();
            y = margin;
          }

          doc.setFont("helvetica", "bold");
          doc.text(themeName, margin, y);
          y += 6;

          if (themeExplanation) {
            doc.setFont("helvetica", "normal");
            const explanationLines = doc.splitTextToSize(themeExplanation, contentWidth);
            doc.text(explanationLines, margin, y);
            y += explanationLines.length * 6 + 8;
          } else {
            y += 8;
          }
        });

        y += 8;
      };

      const addTextSection = (sectionTitle: string, text: string) => {
        const textContent = safeText(text);
        if (!textContent) return; // Skip empty text sections
        
        checkPage();

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(sectionTitle, margin, y);
        y += 8;

        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        const lines = doc.splitTextToSize(textContent, contentWidth);
        
        lines.forEach((line: string) => {
          if (y > 280) {
            doc.addPage();
            y = margin;
          }
          doc.text(line, margin, y);
          y += 6;
        });

        y += 8;
      };

      // Helper: Normalize text for quote matching (same logic as AutoHighlightedText)
      const normalizeForMatching = (str: string): string => {
        return str
          .replace(/[\u2018\u2019'`]/g, "'")
          .replace(/[\u201C\u201D""]/g, '"')
          .replace(/[\u2013\u2014]/g, "-")
          .replace(/[\r\n]+/g, " ")
          .replace(/\s+/g, " ")
          .trim();
      };

      // Add Annotate section with highlighted text
      const addAnnotateSection = () => {
        if (!extractedText || !analysisData) return;
        
        checkPage();

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("Annotated Text", margin, y);
        y += 8;

        // Find all quote positions in the text
        const quotes = analysisData.importantQuotes || [];
        const highlightRanges: Array<{ start: number; end: number }> = [];

        quotes.forEach((q) => {
          const normalizedQuote = normalizeForMatching(q.quote).toLowerCase();
          let normalized = "";
          const positionMap: number[] = [];
          
          for (let i = 0; i < extractedText.length; i++) {
            let char = extractedText[i]
              .replace(/[\u2018\u2019'`]/g, "'")
              .replace(/[\u201C\u201D""]/g, '"')
              .replace(/[\u2013\u2014]/g, "-")
              .replace(/[\r\n]/g, " ");
            
            if (/\s/.test(char)) {
              if (normalized.endsWith(" ")) continue;
              char = " ";
            }
            
            positionMap.push(i);
            normalized += char;
          }
          
          if (normalized.startsWith(" ")) {
            normalized = normalized.slice(1);
            positionMap.shift();
          }
          
          const matchIndex = normalized.toLowerCase().indexOf(normalizedQuote);
          if (matchIndex !== -1) {
            const startOrig = positionMap[matchIndex];
            const endIndex = matchIndex + normalizedQuote.length;
            const endOrig = endIndex < positionMap.length ? positionMap[endIndex] : extractedText.length;
            highlightRanges.push({ start: startOrig, end: endOrig });
          }
        });

        // Sort and remove overlaps
        highlightRanges.sort((a, b) => a.start - b.start);
        const nonOverlapping: typeof highlightRanges = [];
        for (const range of highlightRanges) {
          const last = nonOverlapping[nonOverlapping.length - 1];
          if (!last || range.start >= last.end) {
            nonOverlapping.push(range);
          }
        }

        // Render text with highlights
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        
        let textIndex = 0;
        const lineHeight = 5;
        let currentX = margin;
        const maxX = pageWidth - margin;

        // Process text character by character, handling highlights
        const processText = () => {
          let rangeIndex = 0;
          
          while (textIndex < extractedText.length) {
            // Check if we need a new page
            if (y > 280) {
              doc.addPage();
              y = margin;
              currentX = margin;
            }

            // Check if we're in a highlight range
            let inHighlight = false;
            if (rangeIndex < nonOverlapping.length) {
              const range = nonOverlapping[rangeIndex];
              if (textIndex >= range.start && textIndex < range.end) {
                inHighlight = true;
              } else if (textIndex >= range.end) {
                rangeIndex++;
              }
            }

            // Find the end of the current segment (same style)
            let segmentEnd = extractedText.length;
            if (inHighlight && rangeIndex < nonOverlapping.length) {
              segmentEnd = nonOverlapping[rangeIndex].end;
            } else if (!inHighlight && rangeIndex < nonOverlapping.length) {
              segmentEnd = nonOverlapping[rangeIndex].start;
            }

            // Get the segment text
            const segment = extractedText.slice(textIndex, segmentEnd);
            
            // Skip empty segments to prevent jsPDF errors
            if (!segment || segment.length === 0) {
              textIndex = segmentEnd;
              continue;
            }
            
            const lines = doc.splitTextToSize(segment, contentWidth);

            lines.forEach((line: string, lineIdx: number) => {
              if (y > 280) {
                doc.addPage();
                y = margin;
              }

              if (inHighlight) {
                // Draw yellow background for highlighted text
                const textWidth = doc.getTextWidth(line);
                doc.setFillColor(254, 243, 199); // #FEF3C7
                doc.rect(margin, y - 4, textWidth + 2, lineHeight + 1, 'F');
              }

              doc.setTextColor(0);
              doc.text(line, margin, y);
              y += lineHeight + 1;
            });

            textIndex = segmentEnd;
          }
        };

        processText();
        y += 10;
      };

      // Add selected sections - Annotate first
      if (selectedSections.has("annotate") && extractedText) {
        addAnnotateSection();
      }

      if (selectedSections.has("keyIdeas") && analysisData.keyIdeas?.length) {
        addSection("Key Ideas", analysisData.keyIdeas);
      }

      if (selectedSections.has("themes") && analysisData.themes?.length) {
        addThemeSection("Themes", analysisData.themes);
      }

      if (selectedSections.has("quotes") && analysisData.importantQuotes?.length) {
        addQuoteSection("Important Quotes", analysisData.importantQuotes);
      }

      // Characters section
      if (selectedSections.has("characters") && analysisData.characters?.length) {
        checkPage();
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("Characters", margin, y);
        y += 8;

        doc.setFontSize(11);
        analysisData.characters.forEach((char: any) => {
          const charName = safeText(char.name);
          if (!charName) return; // Skip characters without names
          
          if (y + 30 > 280) {
            doc.addPage();
            y = margin;
          }

          doc.setFont("helvetica", "bold");
          const roleText = safeText(char.role);
          const nameWithRole = roleText ? `${charName} (${roleText})` : charName;
          doc.text(nameWithRole, margin, y);
          y += 6;

          doc.setFont("helvetica", "normal");
          const descText = safeText(char.description);
          if (descText) {
            const descLines = doc.splitTextToSize(descText, contentWidth);
            doc.text(descLines, margin, y);
            y += descLines.length * 6 + 2;
          }

          if (char.traits && Array.isArray(char.traits) && char.traits.length > 0) {
            doc.setTextColor(100);
            // Handle both string traits and object traits
            const traitStrings = char.traits.map((t: any) => {
              if (typeof t === 'string') return t;
              if (typeof t === 'object' && t.trait) return t.trait;
              return '';
            }).filter((t: string) => t);
            
            if (traitStrings.length > 0) {
              const traitsText = `Traits: ${traitStrings.join(", ")}`;
              const traitLines = doc.splitTextToSize(traitsText, contentWidth);
              doc.text(traitLines, margin, y);
              y += traitLines.length * 6;
            }
            doc.setTextColor(0);
          }
          y += 6;
        });
        y += 8;
      }

      // Literary Techniques section
      if (selectedSections.has("literaryTechniques") && analysisData.literaryTechniques?.length) {
        checkPage();
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("Literary Techniques", margin, y);
        y += 8;

        doc.setFontSize(11);
        analysisData.literaryTechniques.forEach((tech: any) => {
          // Handle both 'technique' and 'name' property names
          const techName = safeText(tech.technique || tech.name);
          if (!techName) return; // Skip techniques without names
          
          if (y + 30 > 280) {
            doc.addPage();
            y = margin;
          }

          doc.setFont("helvetica", "bold");
          doc.text(techName, margin, y);
          y += 6;

          doc.setFont("helvetica", "normal");
          const exampleText = safeText(tech.example);
          if (exampleText) {
            doc.setFont("helvetica", "italic");
            const exampleLines = doc.splitTextToSize(`"${exampleText}"`, contentWidth - 10);
            doc.text(exampleLines, margin + 5, y);
            doc.setFont("helvetica", "normal");
            y += exampleLines.length * 6 + 2;
          }

          const explanationText = safeText(tech.explanation);
          if (explanationText) {
            const explLines = doc.splitTextToSize(explanationText, contentWidth);
            doc.text(explLines, margin, y);
            y += explLines.length * 6 + 2;
          }

          const purposeText = safeText(tech.purpose);
          if (purposeText) {
            doc.setTextColor(100);
            const purposeLines = doc.splitTextToSize(`Purpose: ${purposeText}`, contentWidth);
            doc.text(purposeLines, margin, y);
            doc.setTextColor(0);
            y += purposeLines.length * 6;
          }
          y += 6;
        });
        y += 8;
      }

      if (selectedSections.has("summary") && analysisData.summary?.length) {
        addSection("Summary", analysisData.summary);
      }

      if (selectedSections.has("intent") && analysisData.authorIntent) {
        addTextSection("Author Intent", analysisData.authorIntent);
      }

      if (selectedSections.has("techniques") && analysisData.writingTechniques?.length) {
        addSection("Writing Techniques", analysisData.writingTechniques);
      }

      if (selectedSections.has("notes") && notes) {
        addTextSection("Personal Notes", notes);
      }

      // Add watermark for free tier users
      if (tier === 'free' && !isAdmin) {
        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(150, 150, 150);
          doc.text('Generated with AnnotateAI Free Plan - Upgrade at annotateai.org', margin, 287);
        }
      }

      // Generate filename
      const safeTitle = (analysisData.title || "Analysis")
        .replace(/[^a-zA-Z0-9]/g, "-")
        .substring(0, 50);
      const filename = `${safeTitle}-Analysis.pdf`;

      doc.save(filename);
      toast.success("PDF downloaded!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setExporting(false);
    }
  };

  const generateDocx = async () => {
    if (!analysisData) {
      toast.error("No analysis data to export");
      return;
    }

    setExportingDocx(true);
    try {
      const { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle, LevelFormat } = await import('docx');
      const { saveAs } = await import('file-saver');

      const safeText = (text: unknown): string => {
        if (text === null || text === undefined) return "";
        return String(text).trim();
      };

      const children: any[] = [];

      // Header
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: "AnnotateAI", bold: true, size: 28, color: "14B8A6", font: "Arial" }),
            new TextRun({ text: `    ${analysisData.title || "Analysis Export"}`, size: 20, color: "666666", font: "Arial" }),
          ],
        }),
        new Paragraph({
          children: [new TextRun({ text: `Generated on ${new Date().toLocaleDateString()}`, size: 20, color: "808080", font: "Arial" })],
          spacing: { after: 200 },
          border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC", space: 8 } },
        })
      );

      const numbering = {
        config: [
          {
            reference: "bullets",
            levels: [{ level: 0, format: LevelFormat.BULLET as any, text: "\u2022", alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 720, hanging: 360 } } } }],
          },
          {
            reference: "numbered",
            levels: [{ level: 0, format: LevelFormat.DECIMAL as any, text: "%1.", alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 720, hanging: 360 } } } }],
          },
        ],
      };

      const addHeading = (text: string) => {
        children.push(new Paragraph({ heading: HeadingLevel.HEADING_1, spacing: { before: 300, after: 150 }, children: [new TextRun({ text, bold: true, size: 28, font: "Arial", color: "0D9488" })] }));
      };

      // Annotated Text
      if (selectedSections.has("annotate") && extractedText) {
        addHeading("Annotated Text");
        const lines = extractedText.split(/\n+/).filter(l => l.trim());
        lines.forEach(line => {
          children.push(new Paragraph({ spacing: { after: 120 }, children: [new TextRun({ text: line, size: 20, font: "Arial" })] }));
        });
      }

      // Key Ideas
      if (selectedSections.has("keyIdeas") && analysisData.keyIdeas?.length) {
        addHeading("Key Ideas");
        analysisData.keyIdeas.forEach((idea, i) => {
          const t = safeText(idea);
          if (!t) return;
          children.push(new Paragraph({ numbering: { reference: "numbered", level: 0 }, spacing: { after: 80 }, children: [new TextRun({ text: t, size: 22, font: "Arial" })] }));
        });
      }

      // Themes
      if (selectedSections.has("themes") && analysisData.themes?.length) {
        addHeading("Themes");
        analysisData.themes.forEach(theme => {
          const name = safeText(theme.name);
          if (!name) return;
          children.push(new Paragraph({ spacing: { before: 120, after: 40 }, children: [new TextRun({ text: name, bold: true, size: 22, font: "Arial" })] }));
          const exp = safeText(theme.explanation);
          if (exp) children.push(new Paragraph({ spacing: { after: 100 }, children: [new TextRun({ text: exp, size: 22, font: "Arial" })] }));
        });
      }

      // Quotes
      if (selectedSections.has("quotes") && analysisData.importantQuotes?.length) {
        addHeading("Important Quotes");
        analysisData.importantQuotes.forEach(q => {
          const quote = safeText(q.quote);
          if (!quote) return;
          children.push(new Paragraph({ indent: { left: 360 }, spacing: { after: 40 }, children: [new TextRun({ text: `\u201C${quote}\u201D`, italics: true, bold: true, size: 22, font: "Arial" })] }));
          const ctx = safeText(q.context);
          if (ctx) children.push(new Paragraph({ indent: { left: 360 }, spacing: { after: 120 }, children: [new TextRun({ text: `\u2014 ${ctx}`, size: 20, color: "666666", font: "Arial" })] }));
        });
      }

      // Characters
      if (selectedSections.has("characters") && analysisData.characters?.length) {
        addHeading("Characters");
        analysisData.characters.forEach((char: any) => {
          const name = safeText(char.name);
          if (!name) return;
          const role = safeText(char.role);
          children.push(new Paragraph({ spacing: { before: 120, after: 40 }, children: [new TextRun({ text: role ? `${name} (${role})` : name, bold: true, size: 22, font: "Arial" })] }));
          const desc = safeText(char.description);
          if (desc) children.push(new Paragraph({ spacing: { after: 40 }, children: [new TextRun({ text: desc, size: 22, font: "Arial" })] }));
          if (char.traits?.length) {
            const traitStrs = char.traits.map((t: any) => typeof t === 'string' ? t : t?.trait || '').filter(Boolean);
            if (traitStrs.length) children.push(new Paragraph({ spacing: { after: 100 }, children: [new TextRun({ text: `Traits: ${traitStrs.join(", ")}`, size: 20, color: "666666", font: "Arial" })] }));
          }
        });
      }

      // Literary Techniques
      if (selectedSections.has("literaryTechniques") && analysisData.literaryTechniques?.length) {
        addHeading("Literary Techniques");
        analysisData.literaryTechniques.forEach((tech: any) => {
          const name = safeText(tech.technique || tech.name);
          if (!name) return;
          children.push(new Paragraph({ spacing: { before: 120, after: 40 }, children: [new TextRun({ text: name, bold: true, size: 22, font: "Arial" })] }));
          const ex = safeText(tech.example);
          if (ex) children.push(new Paragraph({ indent: { left: 360 }, spacing: { after: 40 }, children: [new TextRun({ text: `\u201C${ex}\u201D`, italics: true, size: 22, font: "Arial" })] }));
          const expl = safeText(tech.explanation);
          if (expl) children.push(new Paragraph({ spacing: { after: 40 }, children: [new TextRun({ text: expl, size: 22, font: "Arial" })] }));
          const purp = safeText(tech.purpose);
          if (purp) children.push(new Paragraph({ spacing: { after: 100 }, children: [new TextRun({ text: `Purpose: ${purp}`, size: 20, color: "666666", font: "Arial" })] }));
        });
      }

      // Summary
      if (selectedSections.has("summary") && analysisData.summary?.length) {
        addHeading("Summary");
        analysisData.summary.forEach((item, i) => {
          const t = safeText(item);
          if (!t) return;
          children.push(new Paragraph({ numbering: { reference: "numbered", level: 0 }, spacing: { after: 80 }, children: [new TextRun({ text: t, size: 22, font: "Arial" })] }));
        });
      }

      // Author Intent
      if (selectedSections.has("intent") && analysisData.authorIntent) {
        addHeading("Author Intent");
        children.push(new Paragraph({ spacing: { after: 120 }, children: [new TextRun({ text: safeText(analysisData.authorIntent), size: 22, font: "Arial" })] }));
      }

      // Writing Techniques
      if (selectedSections.has("techniques") && analysisData.writingTechniques?.length) {
        addHeading("Writing Techniques");
        analysisData.writingTechniques.forEach((t, i) => {
          const text = safeText(t);
          if (!text) return;
          children.push(new Paragraph({ numbering: { reference: "numbered", level: 0 }, spacing: { after: 80 }, children: [new TextRun({ text, size: 22, font: "Arial" })] }));
        });
      }

      // Notes
      if (selectedSections.has("notes") && notes) {
        addHeading("Personal Notes");
        children.push(new Paragraph({ spacing: { after: 120 }, children: [new TextRun({ text: safeText(notes), size: 22, font: "Arial" })] }));
      }

      const doc = new Document({
        numbering,
        styles: {
          default: { document: { run: { font: "Arial", size: 22 } } },
        },
        sections: [{ properties: { page: { size: { width: 12240, height: 15840 }, margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } } }, children }],
      });

      const blob = await Packer.toBlob(doc);
      const safeTitle = (analysisData.title || "Analysis").replace(/[^a-zA-Z0-9]/g, "-").substring(0, 50);
      saveAs(blob, `${safeTitle}-Analysis.docx`);
      toast.success("Word document downloaded!");
    } catch (error) {
      console.error("Error generating DOCX:", error);
      toast.error("Failed to generate Word document");
    } finally {
      setExportingDocx(false);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <Share2 className="h-4 w-4" />
            Share
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-primary" />
            Share & Export
          </DialogTitle>
          <DialogDescription>
            Invite collaborators, share a link, or export your analysis.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="invite" className="gap-1.5 text-xs sm:text-sm">
              <UserPlus className="h-4 w-4" />
              <span className="hidden sm:inline">Invite</span>
            </TabsTrigger>
            <TabsTrigger value="link" className="gap-1.5 text-xs sm:text-sm">
              <Link2 className="h-4 w-4" />
              <span className="hidden sm:inline">Link</span>
            </TabsTrigger>
            <TabsTrigger value="export" className="gap-1.5 text-xs sm:text-sm">
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Export</span>
            </TabsTrigger>
          </TabsList>

          {/* Invite Tab */}
          <TabsContent value="invite" className="space-y-5 py-4">
            <div className="space-y-3">
              <Label htmlFor="invite-email" className="text-sm font-medium">
                Invite others
              </Label>
              <div className="flex gap-2">
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="Enter email addresses..."
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSendInvite()}
                  className="flex-1"
                />
                <Button 
                  onClick={handleSendInvite} 
                  disabled={!inviteEmail.trim() || sendingInvite}
                  size="sm"
                  className="gap-2 px-4"
                >
                  {sendingInvite ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline">Send</span>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Separate multiple emails with commas
              </p>
            </div>

            {/* People with access */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">People with access</Label>
              
              {loadingInvites ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : invitedUsers.length === 0 ? (
                <div className="p-4 rounded-lg bg-secondary/30 border border-border text-center">
                  <UserPlus className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No one else has access yet
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Invite collaborators to view your analysis
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {invitedUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Mail className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {user.email}
                          </p>
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-muted-foreground">
                              {user.accessLevel === "view" ? "View only" : "Can edit"}
                            </p>
                            <span className="text-xs text-muted-foreground">•</span>
                            {user.acceptedAt ? (
                              <span className="text-xs text-green-600 dark:text-green-400">
                                Accepted
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                Invited {formatDistanceToNow(new Date(user.createdAt))} ago
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {!user.acceptedAt && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-muted-foreground hover:text-primary"
                            onClick={() => handleResendInvite(user.id, user.email)}
                            disabled={resendingInviteId === user.id}
                          >
                            {resendingInviteId === user.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3.5 w-3.5" />
                            )}
                            <span className="ml-1 text-xs">Resend</span>
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => handleRemoveInvite(user.id, user.email)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Link Tab */}
          <TabsContent value="link" className="space-y-5 py-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30 border border-border">
              <div className="flex items-center gap-3">
                {isShared ? (
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Globe className="h-5 w-5 text-primary" />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                    <Lock className="h-5 w-5 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Anyone with the link can view
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isShared
                      ? "This analysis is publicly accessible"
                      : "Only invited people can access"}
                  </p>
                </div>
              </div>
              <Switch
                checked={isShared}
                onCheckedChange={handleToggleShare}
                disabled={loading}
              />
            </div>

            {isShared && shareToken ? (
              <div className="space-y-3 animate-fade-in">
                <Label className="text-sm font-medium">Shareable link</Label>
                <div className="flex gap-2">
                  <div className="flex-1 p-3 bg-muted rounded-lg text-sm text-muted-foreground truncate font-mono">
                    {getShareUrl(shareToken)}
                  </div>
                  <Button
                    variant="default"
                    onClick={handleCopyLink}
                    className="gap-2 px-4"
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 space-y-2">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                      Public Link Warning
                    </p>
                  </div>
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    Anyone with this link can view the following content:
                  </p>
                  <ul className="text-xs text-amber-700 dark:text-amber-400 list-disc list-inside space-y-0.5 pl-1">
                    <li>Extracted text from your uploaded pages</li>
                    <li>AI-generated analysis (key ideas, themes, quotes, summary)</li>
                    <li>Author intent and writing techniques</li>
                    <li>All highlights and annotations</li>
                  </ul>
                  <p className="text-xs text-amber-700 dark:text-amber-400 pt-1">
                    Viewers can also leave comments. You can disable sharing at any time to revoke access.
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-secondary/30 border border-border text-center">
                <Lock className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">
                  This analysis is private
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Only you and people you invite can access it
                </p>
              </div>
            )}

            {loading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            )}
          </TabsContent>

          {/* Export Tab */}
          <TabsContent value="export" className="space-y-5 py-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">
                  Select sections to include
                </Label>
                <div className="flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={selectAll}
                    className="text-xs h-7 px-2"
                  >
                    All
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={deselectAll}
                    className="text-xs h-7 px-2"
                  >
                    None
                  </Button>
                </div>
              </div>

              <div className="grid gap-2">
                {exportSections.map((section) => {
                  const Icon = section.icon;
                  const isSelected = selectedSections.has(section.id);
                  
                  return (
                    <button
                      key={section.id}
                      onClick={() => toggleSection(section.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                        isSelected
                          ? "bg-primary/5 border-primary/30"
                          : "bg-secondary/30 border-border hover:bg-secondary/50"
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        className="pointer-events-none"
                      />
                      <Icon className={`h-4 w-4 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                      <span className={`text-sm ${isSelected ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                        {section.label}
                      </span>
                    </button>
                  );
                })}
              </div>

              <p className="text-xs text-muted-foreground text-center">
                <strong className="text-foreground">{selectedSections.size}</strong> section{selectedSections.size !== 1 ? "s" : ""} selected
              </p>
            </div>

            <Button
              onClick={generatePDF}
              disabled={selectedSections.size === 0 || exporting}
              className="w-full gap-2"
              size="lg"
            >
              {exporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating PDF...
                </>
              ) : (
                <>
                  <FileDown className="h-4 w-4" />
                  Export as PDF
                </>
              )}
            </Button>

            <Button
              onClick={generateDocx}
              disabled={selectedSections.size === 0 || exportingDocx}
              variant="outline"
              className="w-full gap-2"
              size="lg"
            >
              {exportingDocx ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating Word...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4" />
                  Export as Word (.docx)
                </>
              )}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
